<template>
  <div style="padding: 20px; background: #fff; min-height: 100vh;">
    <h1 style="color: #409EFF; text-align: center;">🔍 API数据结构分析页面</h1>

    <div style="max-width: 1200px; margin: 0 auto;">
      <div style="margin-bottom: 30px;">
        <h2>📡 API测试</h2>
        <button @click="testRealAPI" :disabled="loading"
                style="padding: 12px 24px; background: #409EFF; color: white; border: none; border-radius: 6px; margin-right: 10px; cursor: pointer;">
          {{ loading ? '测试中...' : '测试真实API' }}
        </button>
        <button @click="clearData"
                style="padding: 12px 24px; background: #F56C6C; color: white; border: none; border-radius: 6px; cursor: pointer;">
          清空数据
        </button>
      </div>

      <div v-if="apiResponse" style="margin-bottom: 30px;">
        <h2>📋 API原始响应数据</h2>
        <div style="background: #f8f9fa; padding: 15px; border-radius: 6px; overflow-x: auto;">
          <pre style="font-size: 12px; margin: 0; white-space: pre-wrap;">{{ JSON.stringify(apiResponse, null, 2) }}</pre>
        </div>
      </div>

      <div v-if="processedOrders.length > 0" style="margin-bottom: 30px;">
        <h2>🔄 处理后的订单数据</h2>
        <div style="background: #e6f7ff; padding: 15px; border-radius: 6px; margin-bottom: 15px;">
          <p><strong>订单总数：</strong>{{ processedOrders.length }}</p>
          <p><strong>总金额：</strong>¥{{ totalAmount }}</p>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead style="background: #f5f7fa;">
            <tr>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">订单号</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">客户</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">金额</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">订单状态</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">支付状态</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">创建时间</th>
              <th style="border: 1px solid #ddd; padding: 12px; text-align: left;">航班信息</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in processedOrders" :key="order.id" style="border: 1px solid #ddd;">
              <td style="border: 1px solid #ddd; padding: 12px; font-family: monospace;">{{ order.orderNo }}</td>
              <td style="border: 1px solid #ddd; padding: 12px;">{{ order.bookerName }}</td>
              <td style="border: 1px solid #ddd; padding: 12px; color: red; font-weight: bold;">¥{{ order.orderTotalAmount.toFixed(2) }}</td>
              <td style="border: 1px solid #ddd; padding: 12px;">
                <span :style="{
                  background: getStatusColor(order.orderStatus),
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }">
                  {{ order.orderStatusText }}
                </span>
              </td>
              <td style="border: 1px solid #ddd; padding: 12px;">
                <span :style="{
                  background: getPaymentStatusColor(order.paymentStatus),
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px'
                }">
                  {{ order.paymentStatusText }}
                </span>
              </td>
              <td style="border: 1px solid #ddd; padding: 12px;">{{ formatDate(order.createTime) }}</td>
              <td style="border: 1px solid #ddd; padding: 12px; font-size: 12px;">
                <div v-if="order.flightInfo.departureCity && order.flightInfo.arrivalCity">
                  {{ order.flightInfo.departureCity }} → {{ order.flightInfo.arrivalCity }}
                </div>
                <div v-if="order.flightInfo.flightNo">
                  {{ order.flightInfo.flightNo }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="error" style="margin-bottom: 30px;">
        <h2>❌ 错误信息</h2>
        <div style="background: #fef0f0; border: 1px solid #fbc4c4; padding: 15px; border-radius: 6px; color: #f56565;">
          <p><strong>错误：</strong>{{ error }}</p>
          <details>
            <summary>详细错误堆栈</summary>
            <pre style="margin-top: 10px; font-size: 12px;">{{ errorStack }}</pre>
          </details>
        </div>
      </div>

      <div style="background: #f0f9ff; padding: 20px; border-radius: 8px; margin-top: 30px;">
        <h3 style="color: #1890ff;">📝 测试说明</h3>
        <p>这个页面用于测试和分析真实API返回的数据结构：</p>
        <ul>
          <li>点击"测试真实API"按钮，调用 <code>http://feihuo.yiqifei.com/OrderAPIV2/GetSalesOrderList</code></li>
          <li>查看API原始响应数据的结构</li>
          <li>查看经过字段映射和数据处理后的结果</li>
          <li>验证字段映射是否正确</li>
          <li>检查状态文本转换是否准确</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { getSalesOrderList, generateOrderListParams } from '@/api/orderListApi'

export default {
  name: 'ApiTestPage',
  data() {
    return {
      loading: false,
      apiResponse: null,
      processedOrders: [],
      error: null,
      errorStack: null
    }
  },
  computed: {
    totalAmount() {
      return this.processedOrders.reduce((sum, order) => sum + (parseFloat(order.orderTotalAmount) || 0), 0).toFixed(2)
    }
  },
  methods: {
    async testRealAPI() {
      this.loading = true
      this.error = null
      this.errorStack = null

      try {
        // 准备测试参数
        const testParams = {
          BookerID: "CMC0MU04", // 用户提供的测试参数
          PageCount: 1,
          PageSize: 10,
          StartDate: "",
          EndDate: "",
          OrderStatus: "",
          OrderNo: ""
        }

        console.log('🚀 开始测试API，参数:', testParams)

        // 生成完整的请求参数
        const fullParams = generateOrderListParams(testParams)
        console.log('📤 完整请求参数:', fullParams)

        // 调用API
        getSalesOrderList(fullParams, (result) => {
          this.loading = false
          console.log('📥 API响应结果:', result)

          this.apiResponse = result

          // 尝试解析数据
          if (result && result.code === 1 && result.data) {
            this.processApiResponse(result)
          } else {
            // 尝试其他可能的响应格式
            this.tryAlternativeResponseFormats(result)
          }
        })

      } catch (error) {
        this.loading = false
        this.error = error.message || '未知错误'
        this.errorStack = error.stack
        console.error('❌ API测试失败:', error)
      }
    },

    processApiResponse(result) {
      try {
        let orderData = []
        let apiData = result.data

        // 尝试不同的数据格式
        if (Array.isArray(apiData)) {
          orderData = apiData
        } else if (apiData.Items && Array.isArray(apiData.Items)) {
          orderData = apiData.Items
        } else if (apiData.SalesOrders && Array.isArray(apiData.SalesOrders)) {
          orderData = apiData.SalesOrders
        } else if (apiData.List && Array.isArray(apiData.List)) {
          orderData = apiData.List
        } else if (apiData.OrderList && Array.isArray(apiData.OrderList)) {
          orderData = apiData.OrderList
        } else {
          // 尝试直接解析为订单数组
          if (typeof apiData === 'object' && Object.keys(apiData).length > 0) {
            orderData = [apiData]
          }
        }

        console.log('📊 解析出的订单数据:', orderData)

        if (orderData.length > 0) {
          // 处理订单数据
          this.processedOrders = this.processOrderData(orderData)
          console.log('✅ 处理后的订单数据:', this.processedOrders)
        } else {
          this.error = 'API响应中未找到有效的订单数据'
        }
      } catch (error) {
        this.error = '处理API响应数据时出错: ' + error.message
        this.errorStack = error.stack
        console.error('❌ 处理响应数据出错:', error)
      }
    },

    tryAlternativeResponseFormats(result) {
      console.log('🔍 尝试其他响应格式:', result)

      // 尝试直接解析为数组
      if (Array.isArray(result)) {
        this.processedOrders = this.processOrderData(result)
        return
      }

      // 尝试其他可能的字段
      const possibleDataFields = ['Items', 'List', 'OrderList', 'SalesOrderList', 'Data', 'data', 'orders']

      for (const field of possibleDataFields) {
        if (result[field] && Array.isArray(result[field])) {
          this.processedOrders = this.processOrderData(result[field])
          return
        }
      }

      this.error = '无法从API响应中解析出订单数据，响应格式不匹配预期格式'
    },

    processOrderData(orders) {
      return orders.map(order => {
        // 提取行程信息 - 从ProductSalesOrders数组的Intro字段合并
        let tripInfo = ''
        if (order.ProductSalesOrders && Array.isArray(order.ProductSalesOrders)) {
          const introList = order.ProductSalesOrders
            .map(subOrder => subOrder.Intro || '')
            .filter(intro => intro.trim() !== '')
          tripInfo = introList.join('；')
        }

        const processedOrder = {
          id: order.ID || order.SalesOrderID || Math.random().toString(),
          orderNo: order.SOShortNr || '', // 用户指定SOShortNr是订单号
          bookerName: order.BookerName || '未知客户',
          orderTotalAmount: parseFloat(order.TotalAmount || order.OrderTotalAmount || 0),
          orderStatus: order.LatestDocStatus || 'New',
          orderStatusText: this.getOrderStatusText(order.LatestDocStatusCName || order.LatestDocStatus),
          paymentStatus: order.PaymentStatus || 'UnPay',
          paymentStatusText: this.getPaymentStatusText(order.PaymentStatus),
          createTime: order.OrderDate ? new Date(order.OrderDate) : new Date(),
          flightInfo: {
            departureCity: order.DepartureCity || '',
            arrivalCity: order.ArrivalCity || '',
            flightNo: order.FlightNo || '',
            flightRoute: tripInfo || '' // 使用合并的行程信息
          },
          rawData: order
        }

        return processedOrder
      })
    },

    // 状态映射方法
    getOrderStatusText(status) {
      const statusMap = {
        'New': '新建',
        'UnPay': '待支付',
        'Paid': '已支付',
        'Ticketed': '已出票',
        'Completed': '已完成',
        'Cancelled': '已取消',
        'Refunded': '已退票',
        'Changed': '已改签',
        'Void': '已作废',
        'Pending': '处理中',
        '待支付': '待支付',
        '已支付': '已支付',
        '已出票': '已出票',
        '已取消': '已取消',
        '已完成': '已完成'
      }
      return statusMap[status] || status || '未知状态'
    },

    getPaymentStatusText(status) {
      const statusMap = {
        'UnPay': '未支付',
        'Pay': '支付中',
        'Paid': '已支付',
        'Refunded': '已退款',
        'PartPaid': '部分支付',
        'PartialPaid': '部分支付',
        'Pending': '支付中',
        '未支付': '未支付',
        '支付中': '支付中',
        '已支付': '已支付',
        '已退款': '已退款',
        '部分支付': '部分支付'
      }
      return statusMap[status] || status || '未知状态'
    },

    getStatusColor(status) {
      const colorMap = {
        'New': '#909399',
        'UnPay': '#E6A23C',
        'Paid': '#409EFF',
        'Ticketed': '#67C23A',
        'Completed': '#67C23A',
        'Cancelled': '#F56C6C',
        'Refunded': '#F56C6C',
        'Changed': '#E6A23C',
        'Void': '#909399',
        'Pending': '#E6A23C',
        '待支付': '#E6A23C',
        '已支付': '#409EFF',
        '已出票': '#67C23A',
        '已取消': '#F56C6C',
        '已完成': '#67C23A'
      }
      return colorMap[status] || '#909399'
    },

    getPaymentStatusColor(status) {
      const colorMap = {
        'UnPay': '#F56C6C',
        'Pay': '#E6A23C',
        'Paid': '#67C23A',
        'Refunded': '#F56C6C',
        'PartPaid': '#E6A23C',
        'PartialPaid': '#E6A23C',
        'Pending': '#E6A23C',
        '未支付': '#F56C6C',
        '支付中': '#E6A23C',
        '已支付': '#67C23A',
        '已退款': '#F56C6C',
        '部分支付': '#E6A23C'
      }
      return colorMap[status] || '#909399'
    },

    formatDate(date) {
      if (!date) return '-'
      try {
        const d = new Date(date)
        if (isNaN(d.getTime())) return date
        return d.toLocaleString('zh-CN')
      } catch (error) {
        return date
      }
    },

    clearData() {
      this.apiResponse = null
      this.processedOrders = []
      this.error = null
      this.errorStack = null
    }
  }
}
</script>

<style scoped>
table {
  font-size: 14px;
}

pre {
  max-height: 400px;
  overflow-y: auto;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>